/**
 * @fileoverview Textarea component for multi-line text input
 * @module ctl-textarea
 * @version 1.0.0
 * 
 * @description
 * A styled textarea component for multi-line text input with support for various configurations
 * including resizing modes, character counting, validation states, and accessibility features.
 * Provides consistent styling and behavior across your application.
 * 
 * The component automatically handles validation states, displays character counts when enabled,
 * and supports different resize behaviors (vertical, horizontal, both, or none). It's designed
 * to work standalone or within a ctl-form-field wrapper for complete form functionality.
 * 
 * @example
 * <!-- Basic textarea -->
 * <ctl-textarea 
 *   label="Comments" 
 *   placeholder="Enter your comments here">
 * </ctl-textarea>
 * 
 * @example
 * <!-- Textarea with character limit -->
 * <ctl-textarea 
 *   label="Description"
 *   maxlength="500"
 *   show-char-count>
 * </ctl-textarea>
 * 
 * @example
 * <!-- Fixed size textarea (no resize) -->
 * <ctl-textarea 
 *   label="Fixed Text"
 *   rows="10"
 *   resize="none">
 * </ctl-textarea>
 * 
 * @example
 * <!-- Textarea with error state -->
 * <ctl-textarea 
 *   label="Required Field"
 *   required
 *   tone="danger"
 *   error-text="This field is required">
 * </ctl-textarea>
 */

/**
 * Textarea Web Component
 * 
 * A styled multi-line text input component with support for validation,
 * character counting, and various resize modes.
 * 
 * @class CtlTextarea
 * @extends HTMLElement
 * 
 * @attr {string} label - Field label text
 * @attr {string} placeholder - Placeholder text
 * @attr {string} value - Current textarea value
 * @attr {number} rows - Number of visible text rows (default: 4)
 * @attr {number} cols - Number of visible text columns
 * @attr {number} maxlength - Maximum character length
 * @attr {number} minlength - Minimum character length
 * @attr {boolean} disabled - Disables the textarea
 * @attr {boolean} readonly - Makes textarea read-only
 * @attr {boolean} required - Marks textarea as required
 * @attr {string} resize - Resize behavior: "none" | "vertical" | "horizontal" | "both" (default: "vertical")
 * @attr {string} tone - Visual tone: "neutral" | "danger" | "warning" | "success" (default: "neutral")
 * @attr {string} help-text - Help text displayed below textarea (read on initial render)
 * @attr {string} error-text - Error message displayed below textarea (read on initial render)
 * @attr {boolean} show-char-count - Shows character count when maxlength is set (read on initial render)
 * 
 * @fires input - Dispatched when textarea value changes
 * @fires change - Dispatched when textarea loses focus after value change
 * @fires ctl-input - Custom event dispatched on input with detail.value property
 * @fires ctl-change - Custom event dispatched on change/blur with detail.value property
 * @fires ctl-blur - Custom event dispatched when textarea loses focus with detail.value property
 * @fires ctl-focus - Custom event dispatched when textarea gains focus with detail.value property
 * 
 * @cssprop --font-family-base - Font family (default: 'Segoe UI', sans-serif)
 * @cssprop --spacing-xs - Extra small spacing (default: 8px)
 * @cssprop --spacing-sm - Small spacing (default: 12px)
 * @cssprop --font-size-sm - Small font size (default: 0.875rem)
 * @cssprop --font-size-md - Medium font size (default: 1rem)
 * @cssprop --font-weight-semibold - Semibold font weight (default: 600)
 * @cssprop --ctl-color-text - Text color (default: #000)
 * @cssprop --ctl-color-surface - Background color (default: #fff)
 * @cssprop --ctl-color-brand - Brand/focus color (default: #6200ee)
 * @cssprop --ctl-color-danger - Error color (default: #b00020)
 * @cssprop --border-color-default - Border color (default: #ccc)
 * @cssprop --border-radius-md - Border radius (default: 8px)
 * @cssprop --transition-normal - Transition duration (default: 0.2s ease)
 */
class CtlTextarea extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this._value = '';
    
    // Bind event handlers for proper cleanup
    this._handleInput = this._handleInput.bind(this);
    this._handleChange = this._handleChange.bind(this);
    this._handleBlur = this._handleBlur.bind(this);
    this._handleFocus = this._handleFocus.bind(this);
    this._listenersAttached = false;
  }

  static get observedAttributes() {
    return ['label', 'placeholder', 'value', 'name', 'rows', 'max-rows', 'cols', 'maxlength', 'minlength', 'disabled', 'readonly', 'required', 'invalid', 'resize', 'tone', 'help-text', 'error-text', 'show-char-count'];
  }

  // Track whether the deprecation warning has already been shown
  static _deprecationWarned = false;

  connectedCallback() {
    if (!CtlTextarea._deprecationWarned) {
      CtlTextarea._deprecationWarned = true;
      if (typeof console !== 'undefined' && console && typeof console.warn === 'function') {
        console.warn(
          '[ctl-textarea] Deprecated: use <ctl-text-field multiline> instead. ' +
          'ctl-textarea will be removed in a future major version.'
        );
      }
    }
    this.render();
    this.attachEventListeners();
  }

  disconnectedCallback() {
    const textarea = this.shadowRoot.querySelector('textarea');
    if (textarea) {
      textarea.removeEventListener('input', this._handleInput);
      textarea.removeEventListener('change', this._handleChange);
      textarea.removeEventListener('blur', this._handleBlur);
      textarea.removeEventListener('focus', this._handleFocus);
    }
    this._listenersAttached = false;
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue !== newValue) {
      this.render();
      this.attachEventListeners();
    }
  }

  get value() {
    return this._value;
  }

  set value(val) {
    this._value = val;
    const textarea = this.shadowRoot.querySelector('textarea');
    if (textarea) {
      textarea.value = val;
    }
  }

  render() {
    this._listenersAttached = false;
    const label = this.getAttribute('label') || '';
    const placeholder = this.getAttribute('placeholder') || '';
    const value = this._value || this.getAttribute('value') || '';
    const name = this.getAttribute('name') || '';
    const rows = this.getAttribute('rows') || '4';
    const cols = this.getAttribute('cols') || '';
    const maxlength = this.getAttribute('maxlength') || '';
    const minlength = this.getAttribute('minlength') || '';
    const disabled = this.hasAttribute('disabled');
    const readonly = this.hasAttribute('readonly');
    const required = this.hasAttribute('required');
    const invalid = this.hasAttribute('invalid');
    const resize = this.getAttribute('resize') || 'vertical';
    const tone = this.getAttribute('tone') || 'neutral';
    const helpText = this.getAttribute('help-text') || '';
    const errorText = this.getAttribute('error-text') || '';
    const showCharCount = this.hasAttribute('show-char-count');

    const resizeStyle = resize === 'none' ? 'none' : resize === 'both' ? 'both' : resize === 'horizontal' ? 'horizontal' : 'vertical';
    
    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: block;
          font-family: var(--font-family-base, 'Segoe UI', sans-serif);
        }

        .field-wrapper {
          display: flex;
          flex-direction: column;
          gap: var(--ctl-space-2);
        }

        label {
          font-size: var(--ctl-font-size-base);
          font-weight: var(--ctl-font-weight-semibold);
          color: var(--ctl-color-text);
          display: flex;
          align-items: center;
          gap: var(--ctl-space-1);
        }

        .required {
          color: var(--ctl-color-danger);
        }

        textarea {
          font-family: inherit;
          font-size: var(--font-size-md, 1rem);
          padding: var(--ctl-input-padding-y) var(--ctl-input-padding-x);
          border: var(--ctl-border-width-thick) solid var(--ctl-color-border);
          border-radius: var(--ctl-radius-md);
          background: var(--ctl-color-surface);
          color: var(--ctl-color-text);
          resize: ${resizeStyle};
          transition: border-color var(--ctl-transition-normal);
          width: 100%;
          box-sizing: border-box;
          line-height: 1.5;
        }

        textarea:focus {
          outline: none;
          border-color: var(--ctl-color-brand);
          box-shadow: 0 0 0 var(--ctl-focus-ring-width) var(--ctl-focus-ring-color);
        }

        textarea:disabled {
          background: var(--ctl-color-surface-2);
          color: var(--ctl-color-muted);
          cursor: not-allowed;
          opacity: var(--ctl-opacity-disabled);
        }

        textarea:readonly {
          background: var(--ctl-color-surface-2);
          cursor: default;
        }

        textarea[data-tone="success"] {
          border-color: var(--ctl-color-success);
        }

        textarea[data-tone="warning"] {
          border-color: var(--ctl-color-warning);
        }

        textarea[data-tone="danger"] {
          border-color: var(--ctl-color-danger);
        }

        .help-text {
          font-size: var(--ctl-font-size-base);
          color: var(--ctl-color-muted);
        }

        .error-text {
          font-size: var(--ctl-font-size-base);
          color: var(--ctl-color-danger);
        }

        .char-count {
          font-size: var(--ctl-font-size-base);
          color: var(--ctl-color-muted);
          text-align: right;
        }

        .char-count.warning {
          color: var(--ctl-color-warning);
        }

        .char-count.danger {
          color: var(--ctl-color-danger);
        }

        .bottom-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
      </style>

      <div class="field-wrapper">
        ${label ? `<label>
          ${label}
          ${required ? '<span class="required">*</span>' : ''}
        </label>` : ''}
        
        <textarea
          placeholder="${placeholder}"
          rows="${rows}"
          ${cols ? `cols="${cols}"` : ''}
          ${maxlength ? `maxlength="${maxlength}"` : ''}
          ${minlength ? `minlength="${minlength}"` : ''}
          ${disabled ? 'disabled' : ''}
          ${readonly ? 'readonly' : ''}
          ${required ? 'required' : ''}
          data-tone="${tone}"
        >${value}</textarea>

        <div class="bottom-row">
          <div>
            ${helpText ? `<div class="help-text">${helpText}</div>` : ''}
            ${errorText ? `<div class="error-text">${errorText}</div>` : ''}
          </div>
          ${showCharCount && maxlength ? `<div class="char-count" id="char-count">0 / ${maxlength}</div>` : ''}
        </div>
      </div>
    `;
  }

  attachEventListeners() {
    if (this._listenersAttached) return;

    const textarea = this.shadowRoot.querySelector('textarea');
    
    if (textarea) {
      textarea.addEventListener('input', this._handleInput);
      textarea.addEventListener('change', this._handleChange);
      textarea.addEventListener('blur', this._handleBlur);
      textarea.addEventListener('focus', this._handleFocus);
      this._listenersAttached = true;
    }
  }

  _handleInput(e) {
    const charCount = this.shadowRoot.querySelector('#char-count');
    const maxlength = this.getAttribute('maxlength');
    
    this._value = e.target.value;
    
    // Update character count
    if (charCount && maxlength) {
      const length = e.target.value.length;
      const max = parseInt(maxlength);
      charCount.textContent = `${length} / ${max}`;
      
      // Add warning/danger classes
      charCount.classList.remove('warning', 'danger');
      if (length >= max * 0.9) {
        charCount.classList.add('warning');
      }
      if (length >= max) {
        charCount.classList.add('danger');
      }
    }

    // Dispatch events
    this.dispatchEvent(new CustomEvent('ctl-input', {
      bubbles: true,
      composed: true,
      detail: {
        value: e.target.value,
        name: this.getAttribute('name') || null,
        originalEvent: e
      }
    }));
  }

  _handleChange(e) {
    this.dispatchEvent(new CustomEvent('ctl-change', {
      bubbles: true,
      composed: true,
      detail: {
        value: e.target.value,
        name: this.getAttribute('name') || null,
        originalEvent: e
      }
    }));
  }

  _handleBlur(e) {
    this.dispatchEvent(new CustomEvent('ctl-blur', {
      bubbles: true,
      composed: true,
      detail: {
        value: e.target.value,
        name: this.getAttribute('name') || null
      }
    }));
  }

  _handleFocus(e) {
    this.dispatchEvent(new CustomEvent('ctl-focus', {
      bubbles: true,
      composed: true,
      detail: {
        value: e.target.value,
        name: this.getAttribute('name') || null
      }
    }));
  }
}

customElements.define("ctl-textarea", CtlTextarea);

export { CtlTextarea };
